import React from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, MapPin, ShieldCheck, Wrench, ArrowLeft } from "lucide-react";
import { useLoad, useTaxonomy } from "../lib/api";
import { catMeta } from "../lib/media";
import { ErrorBox, Loading, StatusBadge } from "../components/kit";
import { StarRow } from "../components/RatingPanel";

export function ProfileFacts({ v, catName, areaName }: any) {
  const facts = [
    [Wrench, `Trades: ${v.category_ids.map(catName).join(", ") || "—"}`],
    [MapPin, `Areas: ${v.area_ids.map(areaName).join(", ") || "—"}`],
    [Clock, `Availability: ${v.availability}`],
    [ShieldCheck, "Approved by Verge operations"],
  ];
  return (
    <ul className="space-y-2 text-sm">
      {facts.map(([Icon, t]: any) => <li key={t} className="flex items-center gap-2"><Icon className="h-4 w-4 text-[var(--blue)]" aria-hidden />{t}</li>)}
    </ul>
  );
}

export function ProfileHeader({ v, title }: { v: any; title: string }) {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-[var(--steel)] bg-white p-3">
      <img src={catMeta(v.category_ids[0]).img} alt="" className="h-20 w-24 rounded-md object-cover" />
      <div><p className="text-lg font-bold text-[var(--navy)]" data-testid="profile-title">{title}</p><StatusBadge value={v.availability} testId="vendor-availability" /></div>
    </div>
  );
}

function PublicRating({ ref_ }: { ref_: string }) {
  const { data } = useLoad<any>(`/public/vendors/${ref_}/rating`);
  if (!data) return null;
  return data.published ? (
    <div className="flex items-center gap-2 text-sm" data-testid="vendor-public-rating"><StarRow value={Math.round(data.average)} idPrefix="pub" /><strong>{data.average}</strong><span className="text-[var(--muted)]">from {data.count} verified completed jobs</span></div>
  ) : <p className="text-xs text-[var(--muted)]" data-testid="vendor-public-rating-none">Not enough verified ratings yet. Ratings show after {data.min_reviews} completed jobs.</p>;
}

export default function VendorPublic() {
  const { slug } = useParams();
  const { data, loading, error, reload } = useLoad<any>(`/public/vendors/${slug}`);
  const { catName, areaName } = useTaxonomy();
  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Link to="/directory" className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--blue)]" data-testid="vendor-back-link"><ArrowLeft className="h-4 w-4" />Back to Results</Link>
      {loading && <Loading />}
      {error && <div className="mt-4"><ErrorBox message={error === "Not found" ? "This provider is not listed." : error} onRetry={reload} /></div>}
      {data && (
        <div className="mt-3 space-y-5" data-testid="vendor-public-profile">
          <h1 className="text-3xl font-extrabold text-[var(--navy)]">Contractor Profile</h1>
          <ProfileHeader v={data} title={data.trade_label} />
          <PublicRating ref_={data.ref} />
          <section className="card space-y-3">
            <h2 className="text-lg font-bold">About This Contractor</h2>
            <p className="whitespace-pre-line text-sm text-[#3b4660]">{data.description || data.headline || "No description yet."}</p>
            <ProfileFacts v={data} catName={catName} areaName={areaName} />
          </section>
          <Link to="/app" className="btn btn-primary btn-lg w-full" data-testid="vendor-request-button">Request This Contractor</Link>
          <p className="text-center text-xs text-[var(--muted)]">The business name stays private until you engage from one of your requests. Your contact information is shared only after you separately approve exactly what to share.</p>
        </div>
      )}
    </div>
  );
}
